import { getApiUrl, withForumOrg, forumPath, forumWriteUrl } from '@/config/api';
import type {
  Subforum,
  Post,
  PostListItem,
  Comment,
  Vote,
  SortOption,
} from '@/features/forum/types';
import { getRecaptchaToken } from '@/utils/recaptcha';

/**
 * The reCAPTCHA action the suggestion form mints its token for. It must equal
 * the API route's `verifyRecaptcha('forum_subforum_request')`: siteverify
 * reports the action, and a mismatch is refused like a missing token.
 */
const SUBFORUM_REQUEST_RECAPTCHA_ACTION = 'forum_subforum_request';

export interface ApiResponse<T> {
  success: boolean;
  statusCode: number;
  data: T;
  message: string;
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

/**
 * The subforum list, plus whether this tenant accepts suggestions for new ones.
 *
 * The flag rides along on a read the page already makes rather than arriving on
 * a probe of its own. A separate "may I suggest?" request would be one more
 * round trip on every forum load to answer a question that only matters for
 * whether one button renders — and it would answer it slightly later than the
 * list, so the button would pop in after the page had settled.
 *
 * OPTIONAL at every level, and absent means NO. Older API builds do not send it,
 * and a missing flag has to read as "not accepting": showing the entry point on
 * a guess puts a visitor through a form that ends in a 404.
 *
 * It sits under `meta`, NOT beside `data`. `data` is the subforum list itself;
 * this is a fact about the organization that happens to ride along on the same
 * read, and the API keeps that distinction rather than flattening a non-list
 * field into the list envelope.
 */
export interface SubforumListResponse extends ApiResponse<Subforum[]> {
  meta?: {
    allowsSubforumRequests?: boolean;
  };
}

/**
 * The `message` an API error carries, or null if it had none.
 *
 * A failed response is not guaranteed to hold JSON at all — a gateway can
 * answer a 429 with HTML — so this never throws and the caller decides what to
 * say when there is nothing to quote.
 */
const readApiMessage = async (response: Response): Promise<string | null> => {
  const body = await response.json().catch(() => null);
  const message = (body as { message?: unknown } | null)?.message;
  return typeof message === 'string' && message.trim() ? message : null;
};

export interface CreatePostRequest {
  subforumId: string;
  title: string;
  content: string;
  author: string;
}

export interface CreateCommentRequest {
  content: string;
  author: string;
  parentId?: string | null;
}

export interface CreateVoteRequest {
  itemId: string;
  itemType: 'post' | 'comment';
  voteType: 'upvote' | 'downvote';
  userId: string;
}

export interface CreateSubforumRequest {
  name: string;
  slug: string;
  description: string;
}

export const forumApi = {
  getSubforums: async (): Promise<SubforumListResponse> => {
    const response = await fetch(getApiUrl(forumPath('/subforums')));
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Failed to fetch subforums' }));
      throw new Error(errorData.message || `Failed to fetch subforums: ${response.status}`);
    }
    return response.json();
  },

  getSubforumBySlug: async (slug: string): Promise<ApiResponse<Subforum>> => {
    const response = await fetch(getApiUrl(forumPath(`/subforums/${slug}`)));
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Failed to fetch subforum' }));
      throw new Error(errorData.message || `Failed to fetch subforum: ${response.status}`);
    }
    return response.json();
  },

  /**
   * SUGGEST a category. This does not create one.
   *
   * The write that used to live here posted to the first-party `/forum/...`
   * route, which requires a session — so for the visitors this site actually
   * serves it was a 401 every time. This goes to the org-addressed PUBLIC
   * surface instead, the same base `forumPath` builds for every read, which is
   * the only forum surface a browser on a tenant's own domain is allowed to
   * reach (see the CORS note in `config/api.ts`).
   *
   * Returns nothing on success, and that is the point: a 202 means the
   * suggestion is queued for an admin to decide on. No subforum exists yet, so
   * there is no row to hand back and NOTHING for a caller to add to its list.
   *
   * The response body is not read even on success — a 202 is allowed to be
   * empty, and `response.json()` on an empty body throws.
   *
   * The route sits behind reCAPTCHA v3 wherever the API enables it (prod does),
   * and refuses a suggestion without a token: "Captcha verification required".
   * The token rides in the BODY, not the `x-recaptcha-token` header. The public
   * CORS policy does not allow that header, so the browser would fail the
   * preflight before the API ever saw the request. The API reads either, and its
   * validation strips the field before the handler runs.
   */
  requestSubforum: async (data: CreateSubforumRequest): Promise<void> => {
    const recaptchaToken = await getRecaptchaToken(SUBFORUM_REQUEST_RECAPTCHA_ACTION);
    const response = await fetch(getApiUrl(forumPath('/subforums')), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(recaptchaToken ? { ...data, recaptchaToken } : data),
    });

    if (response.ok) return;

    const message = await readApiMessage(response);

    // The queue for this tenant is full. The API's own message says what is
    // going on and roughly when it will pass; a generic failure would tell the
    // visitor to retry immediately, which is the one thing that cannot work.
    if (response.status === 429) {
      throw new Error(
        message ?? 'This forum is not accepting suggestions right now. Please try again later.'
      );
    }

    // 404 covers BOTH "this org is not published" and "it does not accept
    // suggestions" — the API refuses to distinguish them on purpose, so that a
    // stranger cannot probe which tenants exist. The copy therefore says what
    // the visitor can act on and claims nothing about which case it was.
    if (response.status === 404) {
      throw new Error('This forum is not accepting category suggestions.');
    }

    throw new Error(message ?? `Could not send that suggestion: ${response.status}`);
  },

  /**
   * One page of a post list. Rows are `PostListItem`: `content` is leaving the
   * list rows, so read the body of an open thread from `getPostById`.
   *
   * `subforumId` omitted lists every space in the organization. `search` is
   * matched on the server (title, author, body) by an API with
   * ai2aim-ems-api#2889; an older build ignores it and answers the unfiltered
   * page, so callers send it only once rows carry `excerpt`
   * (`searchesOnServer`). A blank term sends no `search` key.
   */
  getPosts: async (params: {
    subforumId?: string;
    sort?: SortOption;
    page?: number;
    limit?: number;
    search?: string;
  }): Promise<ApiResponse<PostListItem[]>> => {
    const queryParams = new URLSearchParams();
    if (params.subforumId) {
      queryParams.append('subforumId', params.subforumId);
    }
    if (params.sort) {
      queryParams.append('sort', params.sort);
    }
    if (params.page) {
      queryParams.append('page', params.page.toString());
    }
    if (params.limit) {
      queryParams.append('limit', params.limit.toString());
    }
    const search = params.search?.trim();
    if (search) {
      queryParams.append('search', search);
    }

    const response = await fetch(getApiUrl(forumPath('/posts', queryParams.toString())));
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Failed to fetch posts' }));
      throw new Error(errorData.message || `Failed to fetch posts: ${response.status}`);
    }
    return response.json();
  },

  getPostById: async (postId: string): Promise<ApiResponse<Post>> => {
    const response = await fetch(getApiUrl(forumPath(`/posts/${postId}`)));
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Failed to fetch post' }));
      throw new Error(errorData.message || `Failed to fetch post: ${response.status}`);
    }
    return response.json();
  },

  recordPostView: async (postId: string): Promise<void> => {
    const response = await fetch(getApiUrl(withForumOrg(`forum/posts/${postId}/view`)), {
      method: 'POST',
    });
    if (!response.ok) {
      throw new Error(`Failed to record view: ${response.status}`);
    }
  },

  createPost: async (data: CreatePostRequest): Promise<ApiResponse<Post>> => {
    const response = await fetch(forumWriteUrl('forum/posts'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Failed to create post' }));
      throw new Error(errorData.message || `Failed to create post: ${response.status}`);
    }
    return response.json();
  },

  getComments: async (postId: string): Promise<ApiResponse<Comment[]>> => {
    const response = await fetch(getApiUrl(forumPath(`/posts/${postId}/comments`)));
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Failed to fetch comments' }));
      throw new Error(errorData.message || `Failed to fetch comments: ${response.status}`);
    }
    return response.json();
  },

  createComment: async (postId: string, data: CreateCommentRequest): Promise<ApiResponse<Comment>> => {
    const response = await fetch(forumWriteUrl(`forum/posts/${postId}/comments`), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Failed to create comment' }));
      throw new Error(errorData.message || `Failed to create comment: ${response.status}`);
    }
    return response.json();
  },

  createVote: async (data: CreateVoteRequest): Promise<ApiResponse<Vote>> => {
    const response = await fetch(forumWriteUrl('forum/votes'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Failed to create vote' }));
      throw new Error(errorData.message || `Failed to create vote: ${response.status}`);
    }
    return response.json();
  },
};

